import { request } from "components/hooks/useQuery";
import { getAllIntegrationsBySlug, getAllIntegrationsCardsSlugs } from "queries/Integrations";
import { GetStaticPropsContext } from "next";
import Container from "components/core/Container";
import React from "react";
import { PageSlug } from "types/base";
import { createSubscription } from "components/hooks/usePreview";
import Heading from "components/elements/Heading";
import { Image } from "react-datocms";
import Paragraph from "components/elements/Paragraph";
import Content from "components/blog/blogPost/Content";
import { withAppLayout } from "components/core/withAppLayout";
import useTrans from "components/hooks/useTrans";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "./../../../next-i18next.config";
import IntegrationCard, { IIntegrationsCard } from "components/integrations/IntegrationCard";
import CtaBlock from "../../blocks/cta/CtaBlock";

export async function getStaticPaths({ locales }: any) {
    const { allIntegrationsCards } = await request({
        query: getAllIntegrationsCardsSlugs
    });

    const paths = allIntegrationsCards.map((integrationCard: any) => {
        return locales.map((locale: string) => {
            return { params: { slug: integrationCard.slug }, locale: locale };
        }).flat();
    }).flat();

    return {
        paths: paths,
        fallback: false
    };
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const params = context.params as PageSlug;

    const query = {
        query: getAllIntegrationsBySlug(context.locale, params.slug),
        preview: context.preview,
        withAppData: true,
        locale: context.locale
    };

    const data = await createSubscription(context, query);

    if (!data?.initialData?.integrationsCard) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            data: data,
            seo: data.initialData.integrationsCard.seo,
            appData: {
                ...data.initialData.header,
                ...data.initialData.footer,
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        }
    };
}

function Details(props: any) {
    const card = props.integrationsCard;
    const price = props.integrationsCard.price[0];
    const information = props.integrationsCard.information[0];

    const { t } = useTrans();

    const pricingInfo = () => {
        switch (price?.priceInfo) {
            case 'Starts at':
                return 'startsAt';
            case 'Per location at':
                return 'perLocation';
            case 'Per device':
                return 'perDevice';
            default: return '';
        }
    };

    return (
        <>
            <Container>
                <div className={`grid lg:grid-cols-4 grid-cols-1 grid-rows-none lg:mt-8 `}>
                    <div className={`col-span-3 lg:pr-9 lg:mr-7 pt-32 lg:pt-8`}>
                        <div className={'lg:invisible lg:hidden visible lg:mb-10 mb-6 flex flex-row justify-between space-x-4'}>
                            <div>
                                <Heading renderAs={'h2'} className={'tracking-tight'}>
                                    {card.name}
                                </Heading>
                                <Paragraph
                                    size={'large'}
                                    fontWeight={'light'}
                                    className={'text-gray-text -mt-1 sm:-mt-6'}
                                >
                                    {card.description}
                                </Paragraph>
                            </div>
                            <Image
                                data={card.logo.responsiveImage}
                                pictureClassName={'bg-none object-contain'}
                                className={'lg:w-32 lg:h-32'}
                            />
                        </div>
                        <div className={'invisible hidden lg:visible lg:mb-10 mb-6 lg:flex flex-col justify-between'}>
                            <Image
                                data={card.logo.responsiveImage}
                                pictureClassName={'bg-none object-contain'}
                                className={'lg:w-32 lg:h-32'}
                            />
                            <div>
                                <Heading renderAs={'h2'} className={'tracking-tight'}>
                                    {card.name}
                                </Heading>
                                <Paragraph
                                    size={'large'}
                                    fontWeight={'light'}
                                    className={'text-gray-text -mt-1 sm:-mt-6'}
                                >
                                    {card.description}
                                </Paragraph>
                            </div>
                        </div>
                        <Content
                            content={card.detailsContent}
                            paragraphSize={'medium'}
                            paragraphClassName={'text-gray-text'}
                            headingClassName={'text-purple-dark text-lg lg:text-xl mb-2 lg:mb-3 mt-6 mb-5'}
                        />
                    </div>

                    <div className={`flex lg:relative top-8 border-gray border-2 lg:border-none rounded-xl mt-8`}>
                        <div className={'space-y-4 p-4 w-max'}>
                            
                            {information?.builtBy && (
                                <div>
                                    <Paragraph
                                        size={'extra-small'}
                                        className={'mb-2 text-purple-tint-dark'}
                                    >
                                        {t('builtBy').toUpperCase()}
                                    </Paragraph>
                                    <Paragraph size={'small'} fontWeight={'light'}>
                                        {information.builtBy ? information.builtBy : ''}
                                    </Paragraph>
                                </div>
                            )}

                            {information?.latestUpdate && (
                                <div>
                                    <Paragraph
                                        size={'extra-small'}
                                        className={'mb-2 text-purple-tint-dark'}
                                    >
                                        {t('lastUpdate').toUpperCase()}
                                    </Paragraph>
                                    <Paragraph size={'small'} fontWeight={'light'}>
                                        {information.latestUpdate ? information.latestUpdate : ''}
                                    </Paragraph>
                                </div>
                            )}

                            {information?.languages && (
                                <div>
                                    <Paragraph
                                        size={'extra-small'}
                                        className={'mb-2 text-purple-tint-dark'}
                                    >
                                        {t('languages').toUpperCase()}
                                    </Paragraph>
                                    <Paragraph size={'small'} fontWeight={'light'}>
                                        {information.languages ? information.languages : ''}
                                    </Paragraph>
                                </div>
                            )}

                            <div>
                                <Paragraph
                                    size={'extra-small'}
                                    className={'mb-2 text-purple-tint-dark'}
                                >
                                    {t('price').toUpperCase()}
                                </Paragraph>

                                {price && (
                                    <Paragraph size={'small'} fontWeight={'light'}>
                                        {price.value == 0 ? t('free') : (t(pricingInfo()) + ` ${price.currency}${price.value}`)}
                                    </Paragraph>
                                )}
                            </div>

                            <div>
                                <Paragraph
                                    size={'extra-small'}
                                    className={'mb-2 text-purple-tint-dark'}
                                >
                                    {t('rating').toUpperCase()}
                                </Paragraph>

                                <div className={'flex flex-row space-x-1 items-center'}>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M7.28671 1.19529C7.51122 0.504304 8.48878 0.504305 8.71329 1.19529L10.0768 5.39159C10.1772 5.70061 10.4651 5.90983 10.79 5.90983H15.2023C15.9288 5.90983 16.2309 6.83954 15.6431 7.26659L12.0736 9.86005C11.8107 10.051 11.7007 10.3896 11.8011 10.6986L13.1646 14.8949C13.3891 15.5859 12.5982 16.1605 12.0104 15.7334L8.44084 13.1399C8.17797 12.949 7.82203 12.949 7.55916 13.1399L3.98957 15.7334C3.40179 16.1605 2.61093 15.5859 2.83544 14.8949L4.1989 10.6986C4.29931 10.3896 4.18931 10.051 3.92645 9.86005L0.356858 7.26659C-0.230927 6.83954 0.0711548 5.90983 0.797698 5.90983H5.20995C5.53487 5.90983 5.82284 5.70061 5.92325 5.39159L7.28671 1.19529Z"
                                            fill="#FFD84C"
                                        />
                                    </svg>

                                    <Paragraph size={'small'} fontWeight={'light'} className={'mt-1'}>
                                        {card.rating}
                                    </Paragraph>
                                </div>
                            </div>

                            {card.category.category && (
                                <div>
                                    <Paragraph
                                        size={'extra-small'}
                                        className={'mb-2 text-purple-tint-dark'}
                                    >
                                        {t('categories').toUpperCase()}
                                    </Paragraph>

                                    <Paragraph size={'small'} fontWeight={'light'}>
                                        <span className={'border border-purple-dark-lighter rounded-lg p-1 cursor-default '}>{card.category.category}</span>
                                    </Paragraph>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Container>

            {card.relatedIntegrations.length ? (
                <Container className={'flex mt-8'} theme={'gray'}>
                    <div className={'grid grid-cols-3 gap-x-8 my-auto py-16'}>
                        {card.relatedIntegrations.map((c: IIntegrationsCard, i: number) => <IntegrationCard card={c} key={card.id + i}/>)}
                    </div>
                </Container>
            ) : <div className={'pt-8'}/> }

            {card.cta && (
                <CtaBlock {...card.cta} />
            )}
        </>
    );
}

export default withAppLayout(Details);